import React, { useCallback } from 'react';
import styled from 'styled-components';
import { ButtonSwitch } from 'components';
import { theme, toRem } from 'design';
import { ITipCalculatorResultSplitChecks } from 'types';
import { sortAlphaNumerically } from 'utils/arrays';
import SplitChecksContributing from './SplitCheckContributing';
import SplitChecksReceiving from './SplitChecksReceiving';

interface ISplitChecksToggleableBreakdownProps {
  dataCy?: string;
  splitChecks: ITipCalculatorResultSplitChecks;
  contributingOnly?: boolean;
  disabled?: boolean;
}

const BreakdownContainer = styled.div``;

const StyledButtonSwitch = styled(ButtonSwitch)`
  @media (max-width: 500px) {
    justify-content: space-between;
  }

  @media (max-width: 350px) {
    button {
      padding: ${toRem(theme.space.xs)} ${toRem(theme.space.sm)};
      /* off-scale exception: no font-size token below xxs (11px) */
      font-size: ${toRem(10)};
    }
  }
`;

const ToggleableBreakdownLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
`;

const Header = styled.div`
  display: flex;
  flex-flow: column-reverse nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};

  @media (min-width: 500px) {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: ${toRem(theme.space.lg)} 0 0 0;

  @media (min-width: 500px) {
    margin: 0 ${toRem(theme.space.lg)} 0 0;
  }
`;

const Title = styled.div`
  margin-bottom: ${toRem(theme.space.xs)};
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.bold};
`;

const SplitChecksToggleBreakdown: React.FC<ISplitChecksToggleableBreakdownProps> = ({ dataCy = 'split-checks-toggleable-breakdown', splitChecks, contributingOnly = false, disabled }) => {
  const [switchChecked, setSwitchChecked] = React.useState(!contributingOnly); // true === receiving, false === contributing

  const onSwitchChange = useCallback(() => {
    setSwitchChecked(!switchChecked);
  }, [switchChecked]);

  return (
    <ToggleableBreakdownLayout data-cy={dataCy}>
      <Header data-cy={`${dataCy}-header`}>
        <HeaderInfo>
          <Title data-cy={`${dataCy}-title`}>Split Checks</Title>
        </HeaderInfo>
        {!contributingOnly && <StyledButtonSwitch dataCy={`${dataCy}-switch`} onChange={onSwitchChange} checked={switchChecked} leftText="Contributing" rightText="Receiving" data-cy="toggleable-breakdown-switch" disabled={disabled} />}
      </Header>

      <BreakdownContainer>
        {switchChecked ? <SplitChecksReceiving values={splitChecks.received.sort(sortAlphaNumerically('displayId'))} /> : <SplitChecksContributing values={splitChecks.contributions.sort(sortAlphaNumerically('displayId'))} />}
      </BreakdownContainer>
    </ToggleableBreakdownLayout>
  );
};

export default SplitChecksToggleBreakdown;
